import React, { useState } from "react";
import { C, SIGNS, GLYPH, PLANET_GLYPH, ELEMENT_COLOR } from "./theme";

/* Whole-sign wheel. Ascendant sign cusp sits on the left horizon; zodiac runs
   counterclockwise. Pass positions as {body: eclipticLon}, lots as {name:{lon}}. */
const LOT_SHORT = { Fortune: "⊗", Spirit: "✦", Eros: "E", Necessity: "N",
  Courage: "C", Victory: "V", Nemesis: "X", Death: "D" };
const ASPECT_DEFS = [[60, "#86a06e"], [90, "#e58a9b"], [120, "#6e9cc4"], [180, "#e58a9b"]];

export default function ChartWheel({ positions, lots = null, asc, ascLon = null, size = 340,
                                     showLots: showLotsInit = true, showAspects = true }) {
  const [showLots, setShowLots] = useState(showLotsInit);
  const cx = size / 2, cy = size / 2;
  const R = size / 2 - 6;                       // outer edge
  const signIn = R - 30;                        // inner edge of sign band
  const planetR = signIn - 26;                  // planet glyphs
  const degR = planetR - 20;                    // degree labels
  const hubR = degR - 12;                       // aspect chord endpoints
  const ascStart = SIGNS.indexOf(asc) * 30;

  const theta = (lon) => (180 + (lon - ascStart)) * Math.PI / 180;
  const pt = (lon, r) => [cx + r * Math.cos(theta(lon)), cy - r * Math.sin(theta(lon))];

  const arcPath = (l0, l1, r0, r1) => {
    const [x0, y0] = pt(l0, r1), [x1, y1] = pt(l1, r1);
    const [x2, y2] = pt(l1, r0), [x3, y3] = pt(l0, r0);
    return `M${x0},${y0} A${r1},${r1} 0 0 0 ${x1},${y1} L${x2},${y2} ` +
           `A${r0},${r0} 0 0 1 ${x3},${y3} Z`;
  };

  // collision-relaxed display angles for planet glyphs
  const bodies = Object.entries(positions).sort((a, b) => a[1] - b[1]);
  const disp = bodies.map(([, lon]) => lon);
  for (let pass = 0; pass < 4; pass++)
    for (let i = 0; i < disp.length; i++) {
      const j = (i + 1) % disp.length;
      let gap = (disp[j] - disp[i] + 360) % 360;
      if (gap < 8) { disp[j] = (disp[j] + (8 - gap) / 2) % 360;
                     disp[i] = (disp[i] - (8 - gap) / 2 + 360) % 360; }
    }

  const aspects = [];
  if (showAspects)
    for (let i = 0; i < bodies.length; i++)
      for (let j = i + 1; j < bodies.length; j++) {
        const d = Math.abs(((bodies[i][1] - bodies[j][1] + 180) % 360) - 180);
        for (const [ang, color] of ASPECT_DEFS)
          if (Math.abs(d - ang) <= 3) aspects.push([bodies[i][1], bodies[j][1], color]);
      }

  return (
    <div style={{ display: "inline-block", textAlign: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {SIGNS.map((s, i) => {
          const l0 = i * 30, mid = l0 + 15;
          const [gx, gy] = pt(mid, R - 15);
          const [hx, hy] = pt(mid, hubR - 16);
          const house = (i - SIGNS.indexOf(asc) + 12) % 12 + 1;
          return (
            <g key={s}>
              <path d={arcPath(l0, l0 + 30, signIn, R)} fill={ELEMENT_COLOR(s)}
                fillOpacity="0.13" stroke={C.line} strokeWidth="1" />
              <text x={gx} y={gy} fill={ELEMENT_COLOR(s)} fontSize={size / 24}
                textAnchor="middle" dominantBaseline="central">{GLYPH[s]}</text>
              <text x={hx} y={hy} fill={C.faint} fontSize={size / 34}
                textAnchor="middle" dominantBaseline="central">{house}</text>
            </g>);
        })}
        {Array.from({ length: 36 }).map((_, i) => {
          const [x0, y0] = pt(i * 10, signIn);
          const [x1, y1] = pt(i * 10, signIn - (i % 3 === 0 ? 8 : 4));
          return <line key={i} x1={x0} y1={y0} x2={x1} y2={y1}
            stroke={C.faint} strokeWidth="0.7" opacity="0.6" />;
        })}
        <circle cx={cx} cy={cy} r={hubR} fill="none" stroke={C.line} strokeWidth="0.7" />
        {aspects.map(([a, b, color], k) => {
          const [x0, y0] = pt(a, hubR), [x1, y1] = pt(b, hubR);
          return <line key={k} x1={x0} y1={y0} x2={x1} y2={y1}
            stroke={color} strokeWidth="1" opacity="0.45" />;
        })}
        {showLots && lots && Object.entries(lots).map(([name, v]) => {
          const [x, y] = pt(v.lon, signIn - 13);
          return <text key={name} x={x} y={y} fill={C.brass} fontSize={size / 34}
            textAnchor="middle" dominantBaseline="central" opacity="0.9">
            <title>{`Lot of ${name} — ${Math.floor(v.lon % 30)}° ${SIGNS[Math.floor(v.lon / 30) % 12]}`}</title>
            {LOT_SHORT[name]}</text>;
        })}
        {bodies.map(([name, lon], i) => {
          const [gx, gy] = pt(disp[i], planetR);
          const [dx, dy] = pt(disp[i], degR);
          const [t0x, t0y] = pt(lon, signIn);
          const [t1x, t1y] = pt(lon, signIn - 6);
          return (
            <g key={name}>
              <line x1={t0x} y1={t0y} x2={t1x} y2={t1y} stroke={C.text} strokeWidth="1.2" />
              <text x={gx} y={gy} fill={C.text} fontSize={size / 20}
                textAnchor="middle" dominantBaseline="central">
                <title>{`${name} — ${(lon % 30).toFixed(1)}° ${SIGNS[Math.floor(lon / 30) % 12]}`}</title>
                {PLANET_GLYPH[name] || name[0]}</text>
              <text x={dx} y={dy} fill={C.faint} fontSize={size / 36}
                textAnchor="middle" dominantBaseline="central">{Math.floor(lon % 30)}°</text>
            </g>);
        })}
        {(() => { const [x0, y0] = pt(ascStart, R + 2); const [x1, y1] = pt(ascStart, hubR);
          return <>
            <line x1={x0} y1={y0} x2={x1} y2={y1} stroke={C.brass} strokeWidth="1.4"
              opacity={ascLon != null ? 0.55 : 1} />
            {ascLon == null &&
              <text x={x0 + 4} y={y0 - 6} fill={C.brass} fontSize={size / 30}>ASC</text>}
          </>; })()}
        {ascLon != null && (() => {
          // the Ascendant as a placement: tick + label + degree at its true longitude
          const [t0x, t0y] = pt(ascLon, signIn);
          const [t1x, t1y] = pt(ascLon, signIn - 6);
          const [gx, gy] = pt(ascLon, planetR);
          const [dx, dy] = pt(ascLon, degR);
          return <g>
            <line x1={t0x} y1={t0y} x2={t1x} y2={t1y} stroke={C.brass} strokeWidth="1.4" />
            <text x={gx} y={gy} fill={C.brass} fontSize={size / 28} fontWeight="600"
              textAnchor="middle" dominantBaseline="central">
              <title>{`Ascendant — ${(ascLon % 30).toFixed(1)}° ${SIGNS[Math.floor(ascLon / 30) % 12]}`}</title>
              ASC</text>
            <text x={dx} y={dy} fill={C.brass} fontSize={size / 36}
              textAnchor="middle" dominantBaseline="central">{Math.floor(ascLon % 30)}°</text>
          </g>; })()}
      </svg>
      {lots && (
        <div>
          <button onClick={() => setShowLots(!showLots)}
            style={{ all: "unset", cursor: "pointer", color: C.brass, fontSize: 11 }}>
            {showLots ? "hide" : "show"} Hermetic lots</button>
        </div>
      )}
    </div>
  );
}
